import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Tooltip } from 'antd';
import { FaChevronLeft } from 'react-icons/fa';
import { RiFileList3Line } from 'react-icons/ri';
import JoditEditor from 'jodit-react';
import ApiClient from '../../methods/api/apiClient';
import loader from '../../methods/loader';
import methodModel from '../../methods/methods';
import Layout from '../../components/global/layout';
import FormControl from '../../components/common/FormControl';
import shared from './shared';

import { IoCloseOutline } from 'react-icons/io5';

function AddEditContent() {
  const { id } = useParams();
  const editor = useRef(null);
  const [activeTab, setActiveTab] = useState('editor'); // 'editor' or 'html'
  const [keywordInput, setKeywordInput] = useState('');
  const [form, setForm] = useState({
    heading: '',
    type: '',
    description: '',
    meta_title: '',
    meta_description: '',
    meta_key: '',
    keywords: [],
    image: '',
  });
  const history = useNavigate();
  const [submitted, setSubmitted] = useState(false);

  const config = useMemo(() => ({
    readonly: false,
    placeholder: 'Start typing...',
    height: 400,
    toolbarButtonSize: 'middle',
    buttons: [
      'source', '|',
      'bold', 'italic', 'underline', 'strikethrough', '|',
      'superscript', 'subscript', '|',
      'ul', 'ol', '|',
      'outdent', 'indent', '|',
      'font', 'fontsize', 'brush', 'paragraph', '|',
      'image', 'table', 'link', '|',
      'align', 'undo', 'redo', '|',
      'hr', 'eraser', 'copyformat', '|',
      'fullsize', 'selectall', 'print'
    ],
    uploader: {
      insertImageAsBase64URI: true,
    },
    // To handle the "design pickup" from pasted HTML
    askBeforePasteHTML: false,
    askBeforePasteFromWord: false,
    defaultActionOnPaste: 'insert_clear_html',
  }), []);

  useEffect(() => {
    if (id) {
      loader(true);
      ApiClient.get(shared.detailApi, { id }).then((res) => {
        if (res.success) {
          setForm({
            "title": res?.data?.title,
            "heading": res?.data?.heading,
            "type": res?.data?.type,
            "description": res?.data?.description,
            "meta_title": res?.data?.meta_title,
            "meta_description": res?.data?.meta_description,
            "meta_key": res?.data?.meta_key,
            id: res.data.id || res.data._id,
            keywords: res.data.keywords || (res.data.meta_key ? res.data.meta_key.split(',').map(k => k.trim()) : [])
          });
        }
        loader(false);
      });
    }
  }, [id]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);

    if (!form.heading || !form.description) return;

    loader(true);
    const method = 'put'; // As per user request: api for update /content/update
    const url = shared.editApi;

    ApiClient.allApi(url, form, method).then((res) => {
      if (res.success) {
        history(`/${shared.route}`);
      }
      loader(false);
    });
  };

  const handleEditorChange = (newContent) => {
    setForm({ ...form, description: newContent });
  };

  const addKeyword = () => {
    const val = keywordInput.trim();
    if (val && !form.keywords.includes(val)) {
      const newKeywords = [...form.keywords, val];
      setForm({ ...form, keywords: newKeywords, meta_key: newKeywords.join(',') });
      setKeywordInput('');
    }
  };

  const removeKeyword = (idx) => {
    const newKeywords = form.keywords.filter((_, i) => i !== idx);
    setForm({ ...form, keywords: newKeywords, meta_key: newKeywords.join(',') });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addKeyword();
    }
  };

  return (
    <Layout>
      <form onSubmit={handleSubmit}>
        <div className="pprofile1">
          <div className="flex gap-3 mb-8">
            <Tooltip placement="top" title="Back">
              <Link to={`/${shared.route}`} className="min-w-[30px] w-[30px] h-[30px] rounded-full bg-[#CC9900] text-white flex items-center justify-center hover:opacity-80">
                <FaChevronLeft />
              </Link>
            </Tooltip>
            <div>
              <h3 className="text-lg lg:text-2xl font-semibold text-[#221E10]">
                Edit {form.title?.replace(/_/g, ' ') || 'Content'}
              </h3>
              <p className='text-sm text-[#2D2D2D]'>Update the content information below</p>
            </div>
          </div>

          <div className='grid grid-cols-1 gap-6'>
            {/* Basic Information */}
            <div className='bg-white p-5 rounded-[12px] shadow-[0px_3px_14px_-8px_#888888] border'>
              <div className='mb-5'>
                <div className='flex gap-2'>
                  <RiFileList3Line className="text-[#CC9900] text-xl relative top-[1px]" />
                  <h4 className='text-[#2D2D2D] font-semibold'>Content Information</h4>
                </div>
              </div>
              <div className="grid grid-cols-12 gap-5">
                <div className="col-span-12 sm:col-span-6">
                  <FormControl
                    type="text"
                    label="Heading"
                    value={form.heading}
                    onChange={(e) => setForm({ ...form, heading: e })}
                    required
                  />
                </div>

                <div className="col-span-12">
                  {/* Editor Tabs */}
                  <div className="flex border-b mb-4">
                    <button
                      type="button"
                      className={`px-6 py-2 font-medium ${activeTab === 'editor' ? 'text-primary border-b-2 border-primary' : 'text-gray-500'}`}
                      onClick={() => setActiveTab('editor')}
                    >
                      Editor
                    </button>
                    <button
                      type="button"
                      className={`px-6 py-2 font-medium ${activeTab === 'html' ? 'text-primary border-b-2 border-primary' : 'text-gray-500'}`}
                      onClick={() => setActiveTab('html')}
                    >
                      HTML
                    </button>
                  </div>

                  {activeTab === 'editor' ? (
                    <div className="prose max-w-none">
                      <JoditEditor
                        ref={editor}
                        value={form.description}
                        config={config}
                        onBlur={handleEditorChange}
                      />
                    </div>
                  ) : (
                    <textarea
                      className="w-full h-[400px] p-4 font-mono text-sm bg-gray-50 border rounded-lg focus:ring-primary focus:border-primary"
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      placeholder="Paste your raw HTML here..."
                    />
                  )}
                  {submitted && !form.description && (
                    <div className="text-red-600 text-sm mt-1">Description is required</div>
                  )}
                </div>
              </div>
            </div>

            {/* SEO Information */}
            <div className='bg-white p-5 rounded-[12px] shadow-[0px_3px_14px_-8px_#888888] border'>
              <div className='mb-5'>
                <h4 className='text-[#2D2D2D] font-semibold'>SEO & Meta Data</h4>
              </div>
              <div className="grid grid-cols-12 gap-5">
                <div className="col-span-12 sm:col-span-6">
                  <FormControl
                    type="text"
                    label="Meta Title"
                    value={form.meta_title}
                    onChange={(e) => setForm({ ...form, meta_title: e })}
                  />
                </div>
                <div className="col-span-12 sm:col-span-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Keywords</label>
                  <div className="flex gap-2 mb-2 flex-wrap">
                    {form.keywords.map((tag, i) => (
                      <span key={i} className="inline-flex items-center gap-1 bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-xs border border-gray-200">
                        {tag}
                        <IoCloseOutline className="cursor-pointer hover:text-red-600 text-base" onClick={() => removeKeyword(i)} />
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      className="flex-grow p-2 bg-[#F7F7F7] border rounded-lg focus:ring-0 text-sm"
                      placeholder="Add keyword (press Enter or comma)"
                      value={keywordInput}
                      onChange={(e) => setKeywordInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                    />
                    <button
                      type="button"
                      onClick={addKeyword}
                      className="bg-primary text-white px-4 py-1 rounded-lg text-sm hover:opacity-80"
                    >
                      Add
                    </button>
                  </div>
                </div>
                <div className="col-span-12">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Meta Description</label>
                  <textarea
                    className="w-full p-3 bg-[#F7F7F7] border rounded-lg focus:ring-0"
                    rows="3"
                    value={form.meta_description}
                    onChange={(e) => setForm({ ...form, meta_description: e.target.value })}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-2 justify-end mt-6">
            <button type="submit" className="bg-primary leading-10 h-10 flex justify-center items-center shadow-btn px-10 hover:opacity-80 text-sm text-white rounded-full gap-1 cursor-pointer">
              Update Content
            </button>
          </div>
        </div>
      </form>
    </Layout>
  );
}

export default AddEditContent;
